<script setup lang="ts">
import { computed } from 'vue';
import type { AnnotationDraft } from '@/composables/contractGeneration/useTemplateAnnotations';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';

/**
 * A field used in several places has one set of formatting per tag. This is
 * the "‹ 2 of 5 ›" pager in the Document view's field card: which place is
 * being edited, and arrows to move to the next or previous one — the document
 * scrolls to it and the formatting below follows along.
 */
interface Props {
  occurrences: AnnotationDraft[];
  activeLocalId: string | null;
  disabled: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  select: [localId: string];
}>();

const activeIndex = computed<number>(() =>
  Math.max(
    0,
    props.occurrences.findIndex(occurrence => occurrence.local_id === props.activeLocalId)
  )
);

function step(delta: number): void {
  const index = activeIndex.value + delta;
  const next = index >= 0 ? props.occurrences.at(index) : undefined;
  if (next) emit('select', next.local_id);
}
</script>

<template>
  <n-flex align="center" :size="2" :wrap="false">
    <gaia-icon-button
      quaternary
      size="tiny"
      :dimension="22"
      :disabled="props.disabled || activeIndex === 0"
      aria-label="Previous place"
      @click="step(-1)"
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
      >
        <path d="M7.5 2.5 4 6l3.5 3.5" />
      </svg>
    </gaia-icon-button>
    <gaia-text
      size="meta"
      tone="text"
      :style="{ minWidth: '40px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }"
    >
      {{ activeIndex + 1 }} of {{ props.occurrences.length }}
    </gaia-text>
    <gaia-icon-button
      quaternary
      size="tiny"
      :dimension="22"
      :disabled="props.disabled || activeIndex >= props.occurrences.length - 1"
      aria-label="Next place"
      @click="step(1)"
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
      >
        <path d="M4.5 2.5 8 6l-3.5 3.5" />
      </svg>
    </gaia-icon-button>
  </n-flex>
</template>
